import { useAuth0 } from '@auth0/auth0-react';
import { useMutation as reactUseMutation } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query/src/types';
import axios, { type AxiosResponse, type Method } from 'axios';

import useSnackBar from '../hooks/use-snack-bar.js';

type UseMutationOptions = {
  path: string;
  method?: Method;
  // onSuccess and onError are mutation side-effects
  // See https://react-query.tanstack.com/guides/mutations#mutation-side-effects
  onSuccess?: (data: AxiosResponse<any, any>, variables: Data, context: unknown) => void;
  onError?: (error: unknown, variables: Data, context: unknown) => void;
};

type Data = {
  [key: string]: any;
};

const useMutation = (options: UseMutationOptions) => {
  const { getAccessTokenSilently } = useAuth0();
  const { path, method = 'POST', onError, onSuccess } = options;
  const snackBar = useSnackBar();

  // We pass a unique key to useQuery for caching
  const mutation: UseMutationResult<AxiosResponse<any, any>, unknown, Data, unknown> = reactUseMutation(
    [`${method} ${path}`],
    async (data: Data) => {
      const accessToken = await getAccessTokenSilently();

      if (!accessToken) {
        snackBar.error('You are not logged in. Try refreshing the page.');
      }

      return axios({
        method,
        url: `${import.meta.env.VITE_API_BASE_URL}${path}`,
        data,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
    },
    {
      onError(error, variables, context) {
        // TODO - Log errors here

        snackBar.error('Something went wrong');

        // Custom error callback
        if (onError) {
          onError(error, variables, context);
        }
      },

      onSuccess,
    },
  );

  return mutation;
};

export default useMutation;
